import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '@massilia/ui'

interface SectionWrapperProps {
  className?: string
  children: ReactNode
  description?: string
  icon?: LucideIcon
  title: string
  variant?: 'default' | 'highlight' | 'subtle'
}

export function SectionWrapper({
  className,
  children,
  description,
  icon: Icon,
  title,
  variant = 'default',
}: SectionWrapperProps) {
  return (
    <section
      className={cn(
        'group relative overflow-hidden rounded-2xl border transition-all duration-500',
        'animate-in fade-in slide-in-from-bottom-4',
        {
          'border-amber-200/80 bg-linear-to-br from-amber-50/80 to-orange-50/40 shadow-md':
            variant === 'highlight',
          'border-stone-100 bg-stone-50/50': variant === 'subtle',
          'border-stone-200/60 bg-white shadow-sm hover:shadow-md': variant === 'default',
        },
        className
      )}
    >
      {/* Decorative corner accent */}
      <div
        className={cn(
          'absolute -top-12 -right-12 h-24 w-24 rounded-full opacity-20 blur-2xl transition-opacity duration-500 group-hover:opacity-30',
          {
            'bg-amber-400': variant === 'highlight',
            'bg-brand-500': variant === 'default',
            'bg-stone-300': variant === 'subtle',
          }
        )}
      />

      {/* Rembourrage resserré : ces sections contiennent elles-mêmes des cartes
          qui ont le leur, et l'empilement mangeait la largeur utile sur mobile. */}
      <div className="relative p-4 sm:p-6">
        {/* Section Header */}
        <div className="mb-6 flex items-start gap-3 sm:gap-4">
          {Icon && (
            <div
              className={cn(
                'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-105',
                {
                  'bg-amber-100 text-amber-700': variant === 'highlight',
                  'bg-brand-100 text-brand-700': variant === 'default',
                  'bg-stone-200 text-stone-600': variant === 'subtle',
                }
              )}
            >
              <Icon className="h-5 w-5" strokeWidth={2} />
            </div>
          )}
          <div className="flex-1">
            <h2
              className={cn('font-montserrat text-lg font-semibold tracking-tight', {
                'text-amber-900': variant === 'highlight',
                'text-stone-800': variant !== 'highlight',
              })}
            >
              {title}
            </h2>
            {description && (
              <p className="mt-1 text-sm leading-relaxed text-stone-500">{description}</p>
            )}
          </div>
        </div>

        {/* Section Content */}
        <div className="space-y-4">{children}</div>
      </div>
    </section>
  )
}
